import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, ArrowUpRight, Clock, CheckCircle2, XCircle, X, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Withdrawals() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  const fetchWithdrawals = async () => {
    try {
      const response = await axios.get(`${API_URL}/withdrawals`);
      setWithdrawals(response.data);
    } catch (error) {
      toast.error(t('errors.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    if (!selectedWithdrawal) return;
    
    setCancellingId(selectedWithdrawal.id);
    try {
      await axios.delete(`${API_URL}/withdrawals/${selectedWithdrawal.id}/cancel`);
      toast.success(t('withdrawals.cancelSuccess'));
      setWithdrawals(withdrawals.filter(w => w.id !== selectedWithdrawal.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('withdrawals.cancelError'));
    } finally {
      setCancellingId(null);
      setShowCancelModal(false);
      setSelectedWithdrawal(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="badge-pending flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {t('withdrawals.pending')}
          </span>
        );
      case 'approved':
        return (
          <span className="badge-approved flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            {t('withdrawals.approved')}
          </span>
        );
      case 'rejected':
        return (
          <span className="badge-rejected flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            {t('withdrawals.rejected')}
          </span>
        );
      default:
        return <span className="text-slate-400">{status}</span>;
    }
  };

  const formatDate = (dateStr) => {
    const locale = i18n.language === 'pt' ? 'pt-PT' : i18n.language === 'es' ? 'es-ES' : 'en-GB';
    return new Date(dateStr).toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="animate-fade-in" data-testid="withdrawals-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5 text-slate-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">{t('withdrawals.title')}</h1>
          <p className="text-slate-400 text-sm">{t('withdrawals.subtitle')}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
        </div>
      ) : withdrawals.length > 0 ? (
        <div className="glass-card overflow-hidden">
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr className="bg-white/5">
                  <th>{t('withdrawals.date')}</th>
                  <th>{t('withdrawals.amount')}</th>
                  <th>{t('withdrawals.status')}</th>
                  <th>{t('withdrawals.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.map((withdrawal) => (
                  <tr key={withdrawal.id}>
                    <td className="text-slate-300">
                      {formatDate(withdrawal.created_at)}
                    </td>
                    <td className="text-white font-semibold">
                      {formatCurrency(withdrawal.amount)}
                    </td>
                    <td>{getStatusBadge(withdrawal.status)}</td>
                    <td>
                      {withdrawal.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancelClick(withdrawal)}
                          disabled={cancellingId === withdrawal.id}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid={`cancel-withdrawal-${withdrawal.id}`}
                        >
                          {cancellingId === withdrawal.id ? (
                            <div className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                          ) : (
                            <>
                              <X className="w-4 h-4 mr-1" />
                              {t('withdrawals.cancel')}
                            </>
                          )}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y divide-white/5">
            {withdrawals.map((withdrawal) => (
              <div key={withdrawal.id} className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                      <ArrowUpRight className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">{formatCurrency(withdrawal.amount)}</p>
                      <p className="text-xs text-slate-500">
                        {formatDate(withdrawal.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {getStatusBadge(withdrawal.status)}
                    {withdrawal.status === 'pending' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCancelClick(withdrawal)}
                        disabled={cancellingId === withdrawal.id}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs h-7 px-2"
                      >
                        {cancellingId === withdrawal.id ? (
                          <div className="w-3 h-3 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                        ) : (
                          <>
                            <X className="w-3 h-3 mr-1" />
                            {t('withdrawals.cancel')}
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="glass-card p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
            <ArrowUpRight className="w-8 h-8 text-slate-500" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">{t('withdrawals.noWithdrawals')}</h3>
          <p className="text-slate-400">{t('withdrawals.noWithdrawalsYet')}</p>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && selectedWithdrawal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1F26] rounded-2xl p-6 max-w-md w-full border border-white/10 animate-fade-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{t('withdrawals.cancelWithdrawal')}</h3>
                <p className="text-sm text-slate-400">{t('withdrawals.cannotUndo')}</p>
              </div>
            </div>
            
            <p className="text-slate-300 mb-6">
              {t('withdrawals.cancelConfirm')}{' '}
              <span className="text-white font-semibold">{formatCurrency(selectedWithdrawal.amount)}</span>?
            </p>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedWithdrawal(null);
                }}
                className="flex-1 bg-white/5 hover:bg-white/10 border-white/10"
                data-testid="cancel-modal-no"
              >
                {t('withdrawals.noKeep')}
              </Button>
              <Button
                onClick={handleCancelConfirm}
                disabled={cancellingId}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                data-testid="cancel-modal-yes"
              >
                {cancellingId ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  t('withdrawals.yesCancel')
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
